// src/pages/Farmer/AddFarmer.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import farmerService from '../../services/farmer.service';
import geoService from '../../services/geo.service';
import SearchableSelect from '../../components/SearchableSelect/SearchableSelect';
import Toast from '../../components/Toast/Toast';
import '../../styles/AddPages.css';
import cropService from '../../services/crop.service';

const CROP_OPTIONS = ['Wheat', 'Rice', 'Cotton', 'Sugarcane', 'Soybean', 'Maize', 'Banana', 'Tomato', 'Onion', 'Potato'];
const ACRE_OPTIONS = ['1 to 3 Acres', '4 to 6 Acres', '7 to 10 Acres', '10 to 20 Acres', 'Above 20 Acres', 'Above 100 Acres'];

const AddFarmer = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();

  const [formData, setFormData] = useState({
    farmerName: '',
    mobileNo: '',
    headquarterId: '',
    state: '',
    district: '',
    taluka: '',
    villageMasterId: '',
    crop: '',
    acre: '',
    latitude: '',
    longitude: '',
    currentDate: new Date().toISOString().split('T')[0],
    travelingWith: '',
  });

  const [headquarters, setHeadquarters] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [talukas, setTalukas] = useState([]);
  const [villages, setVillages] = useState([]);
  const [travelingWithOptions, setTraveling] = useState([]);

  const [loading, setLoading] = useState({ hqs: false, st: false, dt: false, tl: false, vl: false });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  // ── Load HQs available to this user ──────────────────────────────────────
  useEffect(() => {
    const cid = isSuperAdmin ? localStorage.getItem('selectedCompanyId') : null;
    if (isSuperAdmin && !cid) return;

    setLoading(l => ({ ...l, hqs: true }));
    farmerService.getMyHeadquarters(cid)
      .then(r => { if (r.success) setHeadquarters(r.data || []); })
      .catch(() => setToast({ message: 'Failed to load HQs', type: 'error' }))
      .finally(() => setLoading(l => ({ ...l, hqs: false })));
  }, [isSuperAdmin]);

  // ── Load states once ─────────────────────────────────────────────────────
  useEffect(() => {
    setLoading(l => ({ ...l, st: true }));
    geoService.getStates()
      .then(r => { if (r.success) setStates(r.data || []); })
      .catch(() => setToast({ message: 'Failed to load states', type: 'error' }))
      .finally(() => setLoading(l => ({ ...l, st: false })));
  }, []);

  const [crops, setCrops] = useState([]);

  useEffect(() => {
    const cid = isSuperAdmin ? localStorage.getItem('selectedCompanyId') : null;
    if (isSuperAdmin && !cid) return;
    cropService.getAllCrops(cid)
      .then(r => { if (r.success) setCrops(r.data || []); })
      .catch(() => { });
  }, [isSuperAdmin]);

  

    // ── State → districts ────────────────────────────────────────────────────
    useEffect(() => {
      setDistricts([]); setTalukas([]); setVillages([]);
      setFormData(f => ({ ...f, district: '', taluka: '', villageMasterId: '' }));
      if (!formData.state) return;
      setLoading(l => ({ ...l, dt: true }));
      geoService.getDistricts(formData.state)
        .then(r => { if (r.success) setDistricts(r.data || []); })
        .finally(() => setLoading(l => ({ ...l, dt: false })));
    }, [formData.state]);

  // ── District → talukas ───────────────────────────────────────────────────
  useEffect(() => {
    setTalukas([]); setVillages([]);
    setFormData(f => ({ ...f, taluka: '', villageMasterId: '' }));
    if (!formData.state || !formData.district) return;
    setLoading(l => ({ ...l, tl: true }));
    geoService.getTalukas(formData.state, formData.district)
      .then(r => { if (r.success) setTalukas(r.data || []); })
      .finally(() => setLoading(l => ({ ...l, tl: false })));
  }, [formData.district]);

  // ── Taluka → villages ────────────────────────────────────────────────────
  useEffect(() => {
    setVillages([]);
    setFormData(f => ({ ...f, villageMasterId: '' }));
    if (!formData.state || !formData.district || !formData.taluka) return;
    setLoading(l => ({ ...l, vl: true }));
    geoService.getVillages(formData.state, formData.district, formData.taluka)
      .then(r => { if (r.success) setVillages(r.data || []); })
      .finally(() => setLoading(l => ({ ...l, vl: false })));
  }, [formData.taluka]);

  // ── Auto-pick first HQ if user only has one ──────────────────────────────
  useEffect(() => {
    if (headquarters.length === 1 && !formData.headquarterId) {
      setFormData(f => ({ ...f, headquarterId: headquarters[0].id }));
    }
  }, [headquarters]);

  // ── Traveling-with employees ─────────────────────────────────────────────
  useEffect(() => {
    const cid = isSuperAdmin ? localStorage.getItem('selectedCompanyId') : null;
    if (isSuperAdmin && !cid) return;
    farmerService.getTravelingWithEmployees(cid)
      .then(res => { if (res.success) setTraveling(res.data || []); })
      .catch(() => { });
  }, [isSuperAdmin]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const detectLocation = () => {
    if (!navigator.geolocation) {
      setToast({ message: 'Geolocation is not supported by your browser.', type: 'error' });
      return;
    }

    setToast({ message: 'Detecting location…', type: 'info' });

    let bestReading = null;
    let settled = false;

    const finish = (position) => {
      if (settled) return;
      settled = true;
      navigator.geolocation.clearWatch(watchId);

      const { latitude, longitude, accuracy } = position.coords;
      setFormData(prev => ({
        ...prev,
        latitude: latitude.toFixed(8),
        longitude: longitude.toFixed(8),
        locationAccuracy: accuracy, // store this on the entity too
      }));

      setToast({ message: '📍 Location detected!', type: 'success' });
    };

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        if (!bestReading || position.coords.accuracy < bestReading.coords.accuracy) {
          bestReading = position;
        }
        // good enough — stop early
        if (position.coords.accuracy <= 15) {
          finish(position);
        }
      },
      (error) => {
        if (settled) return;
        settled = true;
        navigator.geolocation.clearWatch(watchId);
        const message =
          error.code === error.PERMISSION_DENIED
            ? 'Location access denied. Please enable it in your browser/app settings.'
            : error.code === error.TIMEOUT
              ? 'Location request timed out. Please try again.'
              : 'Unable to detect location. ';
        setToast({ message, type: 'error' });
        console.error('Geolocation error:', error);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );

    // Hard stop after ~12s — use best reading we've got rather than hang forever
    setTimeout(() => {
      if (!settled && bestReading) {
        finish(bestReading);
      } else if (!settled) {
        settled = true;
        navigator.geolocation.clearWatch(watchId);
        setToast({ message: 'Could not get an accurate location.', type: 'error' });
      }
    }, 12000);
  };

  const validate = () => {
    const e = {};
    if (!formData.farmerName.trim()) e.farmerName = 'Farmer name is required';
    if (!formData.mobileNo.trim()) e.mobileNo = 'Mobile is required';
    else if (!/^\d{10}$/.test(formData.mobileNo)) e.mobileNo = 'Must be 10 digits';
    if (!formData.headquarterId) e.headquarterId = 'Headquarter is required';
    if (!formData.state) e.state = 'State is required';
    if (!formData.district) e.district = 'District is required';
    if (!formData.taluka) e.taluka = 'Tehsil is required';
    if (!formData.villageMasterId) e.villageMasterId = 'Village is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const pickedVillage = villages.find(v => String(v.id) === String(formData.villageMasterId));

      const payload = {
        ...formData,
        headquarterId: parseInt(formData.headquarterId, 10),
        villageMasterId: parseInt(formData.villageMasterId, 10),
        // Backfill legacy village text column
        village: pickedVillage?.name || '',
        villageCode: pickedVillage?.code || '',
        cropId: formData.cropId ? parseInt(formData.cropId, 10) : undefined,
      };

      if (isSuperAdmin) {
        const cid = localStorage.getItem('selectedCompanyId');
        if (!cid) {
          setToast({ message: 'Please select a company first', type: 'error' });
          setIsSubmitting(false);
          return;
        }
        payload.companyId = parseInt(cid, 10);
      }

      const res = await farmerService.createFarmer(payload);
      if (res.success) {
        setToast({ message: 'Farmer added successfully!', type: 'success' });
        setTimeout(() => navigate('/farmer/view'), 1200);
      }
    } catch (err) {
      setToast({
        message: err.response?.data?.message || 'Failed to add farmer',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-farmer-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><a href="/dashboard/overview">Dashboard</a></li>
          <li className="breadcrumb-item"><a href="/farmer/view">Farmers</a></li>
          <li className="breadcrumb-item active">Add Farmer</li>
        </ol>
      </nav>

      <div className="page-header"><h4 className="page-title">Add New Farmer</h4></div>

      <div className="form-card">
        <form onSubmit={handleSubmit}>

          {/* Basic info */}
          <div className="form-section">
            <h3 className="section-title">👤 Farmer Info</h3>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Farmer Name <span className="required">*</span></label>
                <input type="text" name="farmerName"
                  className={`form-input ${errors.farmerName ? 'error' : ''}`}
                  value={formData.farmerName} onChange={handleChange}
                  placeholder="Enter farmer name" />
                {errors.farmerName && <span className="error-message">{errors.farmerName}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Mobile No <span className="required">*</span></label>
                <input type="tel" name="mobileNo" maxLength="10"
                  className={`form-input ${errors.mobileNo ? 'error' : ''}`}
                  value={formData.mobileNo} onChange={handleChange}
                  placeholder="10-digit mobile" />
                {errors.mobileNo && <span className="error-message">{errors.mobileNo}</span>}
              </div>

              <div className="form-group">
                <SearchableSelect
                  label="Headquarter"
                  required
                  options={headquarters}
                  value={formData.headquarterId}
                  onChange={(id) => {
                    setFormData(prev => ({ ...prev, headquarterId: id }));
                    if (errors.headquarterId) setErrors(prev => ({ ...prev, headquarterId: '' }));
                  }}
                  loading={loading.hqs}
                  placeholder={headquarters.length === 0 && !loading.hqs ? 'No HQs assigned' : 'Search headquarter'}
                  emptyMsg="No headquarters found"
                  error={errors.headquarterId}
                />
              </div>
            </div>
          </div>

          {/* Location cascade */}
          <div className="form-section">
            <h3 className="section-title">📍 Location</h3>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">State <span className="required">*</span></label>
                {loading.st ? (
                  <div className="form-input" style={{ color: '#adb5bd' }}>Loading...</div>
                ) : (
                  <select name="state" className={`form-input ${errors.state ? 'error' : ''}`}
                    value={formData.state}
                    onChange={(e) => setFormData(f => ({ ...f, state: e.target.value }))}>
                    <option value="">Select State</option>
                    {states.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
                  </select>
                )}
                {errors.state && <span className="error-message">{errors.state}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">District <span className="required">*</span></label>
                {loading.dt ? (
                  <div className="form-input" style={{ color: '#adb5bd' }}>Loading...</div>
                ) : (
                  <select name="district" className={`form-input ${errors.district ? 'error' : ''}`}
                    value={formData.district}
                    onChange={(e) => setFormData(f => ({ ...f, district: e.target.value }))}
                    disabled={!formData.state}>
                    <option value="">{!formData.state ? 'Select State first' : 'Select District'}</option>
                    {districts.map(d => <option key={d.name} value={d.name}>{d.name}</option>)}
                  </select>
                )}
                {errors.district && <span className="error-message">{errors.district}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Tehsil/Taluka <span className="required">*</span></label>
                {loading.tl ? (
                  <div className="form-input" style={{ color: '#adb5bd' }}>Loading...</div>
                ) : (
                  <select name="taluka" className={`form-input ${errors.taluka ? 'error' : ''}`}
                    value={formData.taluka}
                    onChange={(e) => setFormData(f => ({ ...f, taluka: e.target.value }))}
                    disabled={!formData.district}>
                    <option value="">{!formData.district ? 'Select District first' : 'Select Tehsil'}</option>
                    {talukas.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                  </select>
                )}
                {errors.taluka && <span className="error-message">{errors.taluka}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group form-group-full">
                <SearchableSelect
                  label="Village"
                  required
                  options={villages.map(v => ({ id: v.id, name: v.code ? `${v.name} (${v.code})` : v.name }))}
                  value={formData.villageMasterId}
                  onChange={(id) => {
                    setFormData(prev => ({ ...prev, villageMasterId: id }));
                    if (errors.villageMasterId) setErrors(prev => ({ ...prev, villageMasterId: '' }));
                  }}
                  disabled={!formData.taluka}
                  loading={loading.vl}
                  placeholder={!formData.taluka ? 'Select Tehsil first' : `Search ${villages.length} villages`}
                  emptyMsg="No villages found"
                  error={errors.villageMasterId}
                />
              </div>
            </div>
          </div>

          {/* Farm details */}
          <div className="form-section">
            <h3 className="section-title">🌾 Farm Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  Crop
                  <a href="/crop/add" target="_blank" rel="noopener noreferrer" style={{ marginLeft: 8, fontSize: 12, fontWeight: 400 }}>
                    + Add New Crop
                  </a>
                </label>
                <select name="cropId" className="form-input" value={formData.cropId} onChange={handleChange}>
                  <option value="">Select Crop</option>
                  {crops.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Acre</label>
                <select name="acre" className="form-input" value={formData.acre} onChange={handleChange}>
                  <option value="">Select Acre Range</option>
                  {ACRE_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Current Date</label>
                <input type="date" name="currentDate" className="form-input"
                  value={formData.currentDate} onChange={handleChange} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Latitude</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="text" name="latitude" className="form-input" style={{ flex: 1 }}
                    value={formData.latitude} onChange={handleChange} placeholder="Latitude" />
                  <button type="button" className="btn btn-secondary" onClick={detectLocation}>📍 Detect</button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Longitude</label>
                <input type="text" name="longitude" className="form-input"
                  value={formData.longitude} onChange={handleChange} placeholder="Longitude" />
              </div>

              <div className="form-group">
                <label className="form-label">Traveling With</label>
                <select name="travelingWith" className="form-input"
                  value={formData.travelingWith} onChange={handleChange}>
                  <option value="">Select Name</option>
                  {travelingWithOptions.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary"
              onClick={() => navigate('/farmer/view')} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Adding Farmer...' : 'Add Farmer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddFarmer;